import Link from "next/link";
import classNames from "classnames";
import getConfig from "next/config";

import Price from "@components/Price";
import Ratings from "@components/Ratings";
import ProductCardLabel from "@components/ProductCardLabel";
import ProductListImage from "@components/ProductList/ProductListImage";
import ProductListTitle from "@components/ProductList/ProductListTitle";
import AddToCompare from "@containers/Catalog/Actions/AddToCompare";
import AddToWishlist from "@containers/Catalog/Actions/AddToWishlist";
import { productHelper } from "@lib/helpers";
import ProductButtonViewResealed from "@components/ProductButtonViewResealed";
import ProductListColorVariations from "@components/ProductList/ProductListColorVariations";

const {
  publicRuntimeConfig: {
    settings: { websiteName },
  },
} = getConfig();

const isBricoStore = websiteName === "bricostore";

// Brico: offer label top-left; favorite/compare top-RIGHT and HIGH — identical to the
// category card (ProductListItem.js). The label's right edge (right-[52px]) stops before the
// ~32px buttons so it never runs under them.
const cornerLabelPos = isBricoStore
  ? "top-10px left-11px right-[52px] lg:top-15px lg:left-16px items-start"
  : "top-0 right-0 items-end";
const actionsPos = isBricoStore
  ? "top-10px right-11px lg:top-15px lg:right-16px"
  : "top-10px left-11px lg:top-15px lg:left-16px";
// Brico: don't clip the offer label's point/eyelet + small gap between labels; width is
// bounded by the left/right positions above (max-w-[85%] stays as a safety cap)
const cornerLabelBox = isBricoStore
  ? "overflow-visible gap-1 max-w-[85%]"
  : "truncate max-w-[75%]";

const ProductListResealedItem = ({
  product,
  deleteFromWishlist,
  listType,
  onProductClick,
  hasColorVariationsInCurrentRow,
}) => {
  const {
    url_key,
    sku,
    thumbnail,
    name,
    regular_price,
    lowest_price,
    msrp_price,
    price,
    discount_type,
    reviews_count,
    reviews_value,
    configurable_options,
  } = product;

  const hasColorVariations =
    configurable_options?.length &&
    configurable_options.find((opt) => opt.attribute_code === "culoare_global");

  const { isDisabled, getLabels, hasPricePerFactor, hasPriceUnit } =
    productHelper(product);

  const onRemoveFromWishlist = (event) => {
    event.preventDefault();
    deleteFromWishlist(product);
  };

  return (
    <li
      className={classNames(
        "Products-item w-1/2 sm:w-1/3 p-1 sm:p-2 min-h-330px lg:min-h-400px bg-white",
        {
          "md:w-1/3": listType === "wishlist",
          "lg:w-1/4": listType !== "wishlist",
          "border-transparent md:border-2": websiteName !== "bricostore",
        }
      )}
    >
      <div
        className={classNames("Product flex flex-col relative", {
          "hover:shadow-mixRedYellow": websiteName !== "bricostore",
          "shadow-raised sm:hover:shadow-raised-hover border-0 rounded-[8px]":
            websiteName === "bricostore",
        })}
        data-test="PRODUCT_CARD"
        onClick={() => onProductClick(product)}
      >
        <Link href={`/${url_key}/cpd/${sku}/#resigilate`}>
          <a title="">
            <ProductListImage thumbnail={thumbnail} name={name} />
          </a>
        </Link>

        <Link href={`/${url_key}/cpd/${sku}/#resigilate`}>
          <a title="">
            <ProductListTitle name={name} />
          </a>
        </Link>

        {!isDisabled && (
          <>
            {getLabels().length !== 0 && (
              <div
                className={`absolute ${cornerLabelPos} h-auto w-auto flex flex-col ${cornerLabelBox}`}
              >
                {/* Brico: drop the empty slot getLabels() appends (keep any real labels) */}
                {(isBricoStore
                  ? getLabels().filter((label) => label.title)
                  : getLabels()
                ).map(({ title, variant }, index) => (
                  <ProductCardLabel
                    key={`${title}-${index}`}
                    variant={variant}
                    className="truncate max-w-full"
                  >
                    {title}
                  </ProductCardLabel>
                ))}
              </div>
            )}

            {hasColorVariationsInCurrentRow && (
              <div className="mt-[8px] h-[56px] overflow-auto scrollbar-none">
                {hasColorVariations && (
                  <ProductListColorVariations product={product} />
                )}
              </div>
            )}

            <span className="leading-tight text-13px text-[#39AB4A] -tracking-0.27 h-18px font-semibold">
              resigilat
            </span>

            <div className="h-[20px] my-[4px]">
              <Ratings
                reviewsCount={reviews_count}
                ratingSummary={reviews_value}
                displayReviewsNo
                productUrl={`/${url_key}/cpd/${sku}/`}
              />
            </div>

            <div className="mb-0 mt-auto">
              <Price
                price={price}
                regularPrice={regular_price}
                lowestPrice={lowest_price}
                msrp={msrp_price}
                formatted
                discountType={discount_type}
                displayDiscount
                size="smaller"
                resealedFilter
                displayUnit={
                  hasPricePerFactor && hasPriceUnit
                    ? product.price_unit.split("/")[1]
                    : false
                }
              />
            </div>

            <div className="mt-14px sm:mt-13px">
              <ProductButtonViewResealed
                href={`/${url_key}/cpd/${sku}/#resigilate`}
                isCatalog
              />
            </div>
          </>
        )}

        <div
          className={`absolute ${actionsPos} flex flex-col justify-center items-center space-y-1.5`}
        >
          <AddToWishlist product={product} />
          <AddToCompare product={product} />
        </div>

        {listType === "wishlist" && (
          <button
            className="mt-3 text-red-brand hover:text-bleu-hover underline text-sm outline-none"
            onClick={onRemoveFromWishlist}
          >
            Sterge
          </button>
        )}
      </div>
    </li>
  );
};

export default ProductListResealedItem;
